import {
  OverridableQuery,
  UseQueryOptions,
  UseMutationOptions,
  serialize,
  useQuery,
  useMutation,
  restrictApi,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { IS2SBusinessRule } from '~/connect/types';
import { IS2SBusinessRulePayload } from '~/connect/types';
import {
  IErrorApiResponse,
  IPaginationResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import { ConnectQueryKeys } from '~/connect/api';
import { CommonErrorCodesEnumType } from '~/common/types';
import { PAGINATE_ALL_LIMIT } from '~/common/helpers';

type PaginateArgs = {
  page: number;
  limit: number;
  query?: string;
};

type Options = {
  getAll?: OverridableQuery<
    UseQueryOptions<IPaginationResponse<IS2SBusinessRule>, IErrorApiResponse>
  >;
  paginate?: OverridableQuery<
    UseQueryOptions<IPaginationResponse<IS2SBusinessRule>, IErrorApiResponse>
  > & { args?: PaginateArgs };
  getOneById?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<IS2SBusinessRule>, IErrorApiResponse>
  > & { args?: { id: string } };
  update?: UseMutationOptions<
    ISuccessApiResponse<IS2SBusinessRule>,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >,
    { id: string; body: IS2SBusinessRulePayload }
  >;
};

export const useManageS2SBusinessRules = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const endpoint = `/tenants/api/wtb/tenants/${tenantCode}/s2s-business-rules`;

  const getAll = useQuery({
    queryKey: ConnectQueryKeys.s2sBusinessRules.base(),
    queryFn: () =>
      callCoreApi<IPaginationResponse<IS2SBusinessRule>>(
        `${endpoint}?${serialize({ limit: PAGINATE_ALL_LIMIT })}`,
      ),
    ...options.getAll,
  });

  const paginate = useQuery({
    queryKey: ConnectQueryKeys.s2sBusinessRules.paginate(
      options.paginate?.args,
    ),
    queryFn: () =>
      callCoreApi<IPaginationResponse<IS2SBusinessRule>>(
        `${endpoint}?${serialize(options.paginate?.args ?? {})}`,
      ),
    ...options.paginate,
  });

  const getOneById = useQuery({
    queryKey: ConnectQueryKeys.s2sBusinessRules.detail(
      options.getOneById?.args?.id,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IS2SBusinessRule>>(
        `${endpoint}/${options.getOneById!.args!.id}`,
      ),
    enabled: !!options.getOneById?.args?.id,
    ...options.getOneById,
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: IS2SBusinessRulePayload }) =>
      callCoreApi<ISuccessApiResponse<IS2SBusinessRule>>(`${endpoint}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    keysToInvalidateOnSuccess: ({ id }) => [
      ConnectQueryKeys.s2sBusinessRules.detail(id),
      ConnectQueryKeys.s2sBusinessRules.base(),
    ],
    ...options.update,
  });

  return restrictApi(options, {
    getAll,
    paginate,
    getOneById,
    update,
  });
};
